import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// Reusable Cinematic Parallax Hero for ALL subpages
const ParallaxHero = ({
  image,
  tag,
  title,
  titleHighlight,
  subtitle,
  overlayColor = "rgba(5,5,5,0.7)",
  accentColor = "rgba(212,175,55,0.12)",
  children
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const words = title ? title.split(" ") : [];

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.09, delayChildren: 0.5 } },
  };
  const word = {
    hidden: { opacity: 0, y: 50, filter: "blur(15px)" },
    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-20">
      {/* Parallax Image Layer */}
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <img src={image} alt={title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${overlayColor} 0%, rgba(5,5,5,0.85) 60%, #050505 100%)` }} />
        {/* Cinematic Radial Accent */}
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 40%, ${accentColor} 0%, transparent 65%)` }} />
        {/* Film grain overlay */}
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")', backgroundSize: '256px' }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Tag */}
        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.1em" }}
          animate={{ opacity: 1, letterSpacing: "0.35em" }}
          transition={{ duration: 1.8, delay: 0.2 }}
          className="text-gold uppercase text-xs md:text-sm mb-8 font-semibold"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.6)' }}
        >
          {tag}
        </motion.p>

        {/* Cinematic Word-by-Word Reveal */}
        <motion.h1
          variants={container}
          initial="hidden"
          animate="visible"
          className="text-5xl md:text-7xl lg:text-8xl font-serif text-cream leading-tight mb-6 flex flex-wrap justify-center gap-x-5 gap-y-2"
        >
          {words.map((w, i) => (
            <motion.span key={i} variants={word}
              className={w === titleHighlight ? "italic text-transparent bg-clip-text bg-gold-gradient drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" : ""}
            >
              {w}
            </motion.span>
          ))}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="text-lg md:text-xl text-cream/75 max-w-2xl mx-auto mb-12 font-light leading-relaxed"
          >
            {subtitle}
          </motion.p>
        )}

        {children}
      </motion.div>

      {/* Cinematic Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 14, 0], opacity: [0.2, 1, 0.2] }}
        transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <div className="w-1 h-1 rounded-full bg-gold" style={{ boxShadow: '0 0 12px rgba(212,175,55,1)' }} />
        <div className="w-[1px] h-20 bg-gradient-to-b from-gold via-gold/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default ParallaxHero;